import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, Download, FileAudio, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { useLanguage } from '../../context/LanguageContext';
import { myiaTexts } from '../../utils/myiaTranslations';
import { usePremiumEnabled } from '../../hooks/usePremiumEnabled';
import { ReportAIContentButton } from '../ReportAIContent';

/**
 * AudioExtractor — Accept a short (<5 min) video and extract MP3 audio.
 */
export default function AudioExtractor() {
  const { language } = useLanguage();
  const M = myiaTexts(language);
  const premiumOn = usePremiumEnabled();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { url, filename }
  const inputRef = useRef(null);

  const pickFile = () => inputRef.current?.click();

  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) {
      toast.error(M.axTooBig);
      return;
    }
    setFile(f);
    setResult(null);
  };

  const extract = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('video', file);
      const r = await axios.post('/api/myia-voice/extract-audio', fd, { timeout: 300000 });
      const { audio_b64, filename, duration_sec } = r.data || {};
      if (!audio_b64) throw new Error('no audio');
      const url = `data:audio/mp3;base64,${audio_b64}`;
      setResult({ url, filename: filename || 'audio.mp3', duration: duration_sec });
      toast.success(M.axOk);
    } catch (e) {
      toast.error(e?.response?.data?.detail || M.axKo);
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!result) return;
    const filename = result.filename || 'audio.mp3';

    // Native iOS/Android: WKWebView does not honour <a download> on data URIs,
    // so we write to the app cache and open the native share sheet.
    if (Capacitor.isNativePlatform()) {
      try {
        const b64 = String(result.url).split(',')[1] || '';
        const written = await Filesystem.writeFile({
          path: filename,
          data: b64,
          directory: Directory.Cache,
        });
        await Share.share({
          title: filename,
          text: 'Extraction audio Destine',
          url: written.uri,
          dialogTitle: 'Enregistrer ou partager le MP3',
        });
        return;
      } catch (e) {
        // Fall through to <a> click as a last resort.
        console.error('native audio download failed', e);
        toast.error("Impossible d'enregistrer le fichier. Réessayez.");
      }
    }

    // Web (or native fallback): trigger a normal download.
    const a = document.createElement('a');
    a.href = result.url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="p-4 rounded-2xl bg-gradient-to-br from-lime-500/10 to-green-500/10 border border-lime-500/30" data-testid="audio-extractor-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="text-2xl shrink-0">🎧</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-lime-200 text-sm">{M.axTitle}</h4>
            {premiumOn && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-[9px] font-bold text-yellow-300 uppercase tracking-wide" data-testid="audio-extractor-premium-badge">
                <Crown className="w-2.5 h-2.5" /> Premium
              </span>
            )}
          </div>
          <p className="text-[11px] text-lime-200/70 mt-0.5 leading-snug">
            {M.axSub}
          </p>
        </div>
      </div>

      <input ref={inputRef} type="file" accept="video/*" onChange={onChange} className="hidden" data-testid="audio-extractor-input" />

      {!file && (
        <button
          onClick={pickFile}
          className="w-full py-3 rounded-full bg-gradient-to-r from-lime-500 to-green-600 font-semibold text-xs flex items-center justify-center gap-2"
          data-testid="audio-extractor-pick"
        >
          <Upload className="w-4 h-4" /> {M.axPick}
        </button>
      )}

      {file && !result && (
        <div className="space-y-2">
          <div className="p-2.5 rounded-xl bg-black/30 border border-lime-500/20 text-xs text-lime-100 truncate">
            <FileAudio className="w-3.5 h-3.5 inline mr-1" /> {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
          </div>
          <div className="flex gap-2">
            <button onClick={() => setFile(null)} className="flex-1 py-2 rounded-full bg-white/10 text-xs" data-testid="audio-extractor-cancel">{M.axCancel}</button>
            <button
              onClick={extract} disabled={loading}
              className="flex-1 py-2 rounded-full bg-gradient-to-r from-lime-500 to-green-600 text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid="audio-extractor-run"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {loading ? M.axRunning : M.axRun}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-2">
          <audio src={result.url} controls className="w-full h-8" />
          <div className="flex gap-2">
            <button onClick={() => { setFile(null); setResult(null); }} className="flex-1 py-2 rounded-full bg-white/10 text-xs" data-testid="audio-extractor-restart">
              {M.axOther}
            </button>
            <ReportAIContentButton feature="audio_extractor" iconOnly className="p-2 rounded-full bg-white/10 hover:bg-orange-500/30 text-orange-200 flex-shrink-0" testId="ai-report-audio-extractor" />
            <button onClick={download} className="flex-1 py-2 rounded-full bg-gradient-to-r from-lime-500 to-green-600 text-xs font-semibold flex items-center justify-center gap-1" data-testid="audio-extractor-download">
              <Download className="w-3.5 h-3.5" /> {M.axDownload}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
